import { useEffect } from 'react';

import { fg } from '@confluence/feature-gating';
import type { CommentType } from '@confluence/comments-data';
import type { ThreadKeyToCommentIdMap, UnreadComment } from '@confluence/unread-comments';

type UseMarkVisibleRepliesAsReadParams = {
	isCurrentSelectedComment: boolean;
	visibleUnreadRepliesNotMarkedAsRead: string[];
	threadKey: string;
	commentType: CommentType;
	setCommentsInThreadToBeMarkedAsRead: ({
		threadKey,
		commentIds,
		commentType,
	}: {
		threadKey?: string;
		commentIds?: string[];
		commentType: CommentType;
	}) => void;
	updateUnreadCommentsListState: (
		transform: (
			prevUnreadCommentsListState: UnreadComment[],
			threadKeyToCommentIdMap: ThreadKeyToCommentIdMap,
		) => UnreadComment[],
	) => void;
};

export const useMarkVisibleRepliesAsRead = ({
	isCurrentSelectedComment,
	visibleUnreadRepliesNotMarkedAsRead,
	threadKey,
	commentType,
	setCommentsInThreadToBeMarkedAsRead,
	updateUnreadCommentsListState,
}: UseMarkVisibleRepliesAsReadParams) => {
	// if selecting the comment thread, we need to mark all visible replies as read as well
	// we'll let setCommentsInThreadToBeMarkedAsRead handle duplicates if any
	useEffect(() => {
		if (
			isCurrentSelectedComment &&
			visibleUnreadRepliesNotMarkedAsRead.length > 0 &&
			fg('confluence_frontend_mark_uncollapsed_replies_read')
		) {
			setCommentsInThreadToBeMarkedAsRead({
				threadKey,
				commentIds: visibleUnreadRepliesNotMarkedAsRead,
				commentType,
			});
			updateUnreadCommentsListState((prev) => {
				const repliesIds = new Set(visibleUnreadRepliesNotMarkedAsRead);
				return prev.filter((unreadComment) => {
					if (repliesIds.has(unreadComment.id)) {
						return false;
					}
					return true;
				});
			});
		}
	}, [
		commentType,
		isCurrentSelectedComment,
		setCommentsInThreadToBeMarkedAsRead,
		threadKey,
		updateUnreadCommentsListState,
		visibleUnreadRepliesNotMarkedAsRead,
	]);

	// 👇 To be removed once change is soaked 👇
	useEffect(() => {
		if (isCurrentSelectedComment && !fg('confluence_frontend_mark_uncollapsed_replies_read')) {
			setCommentsInThreadToBeMarkedAsRead({
				threadKey,
				commentIds: visibleUnreadRepliesNotMarkedAsRead,
				commentType,
			});
			updateUnreadCommentsListState((prev) => {
				const repliesIds = new Set(visibleUnreadRepliesNotMarkedAsRead);
				return prev.filter((unreadComment) => {
					if (repliesIds.has(unreadComment.id)) {
						return false;
					}
					return true;
				});
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- there might be a better way to handle this without needing a useEffect
	}, [isCurrentSelectedComment]);
};
